import React, { useMemo, useEffect } from "react";

import {
  createBrowserRouter,
  RouterProvider,
  Outlet,
  useLocation,
  Navigate,
} from "react-router-dom";
import "./global.scss";

import AppShell from "./components/AppShell/AppShell";
import Loader from "./components/Loader/Loader";
import ProtectedRoute from "./components/ProtectedRoute/ProtectedRoute";
import { useAuth } from "./context/AuthContext";
import { usePagePermissions } from "./context/PagePermissionsContext";
// Keep LoginPage, ErrorPage, and TwoFactorVerifyPage as regular imports (small, frequently accessed)
import ErrorPage from "./pages/ErrorPage/ErrorPage";
import ForgotPassword from "./pages/ForgotPasswordPage/ForgotPassword";
import LoginPage from "./pages/LoginPage/LoginPage";
import ResetPassword from "./pages/ResetPasswordPage/ResetPassword";
import SetPassword from "./pages/SetPasswordPage/SetPassword";
import TwoFactorVerifyPage from "./pages/TwoFactorVerifyPage/TwoFactorVerifyPage";
import { logger } from "./utils/logger";

// Lazy load pages - using dynamic imports that don't block navigation
// Industry standard: Use dynamic imports with state management instead of React.lazy()
// This ensures navigation happens immediately
const loadProfilePage = () => import("./pages/ProfilePage/ProfilePage");
const loadExecutiveDashboardPage = () =>
  import("./pages/ExecutiveDashboardPage/ExecutiveDashboard");
const loadMainPage = () => import("./pages/MainPage/MainPage");
const loadThreatMPage = () => import("./pages/ThreatMapPage/ThreatMap");
const loadPostureManagement = () => import("./pages/PostureManagementPage/PostureManagement");
const loadAriaPage = () => import("./pages/AriaPage/Aria");
const loadAriaDiagnosticsPage = () =>
  import("./pages/AriaDiagnosticsPage/AriaDiagnostics");
const loadClientHub = () => import("./pages/ClientHubPage/ClientHub");
const loadAIInventory = () => import("./pages/GovernancePage/AIInventory/AIInventory");
const loadAIAdoption = () => import("./pages/GovernancePage/AIAdoption/AIAdoption");
const loadDataRisks = () => import("./pages/GovernancePage/DataRisks/DataRisks");
const loadHelpPage = () => import("./pages/HelpPage/HelpPage");
const loadTprmPage = () => import("./pages/GovernancePage/TprmPage/TprmPage");
// Component cache to prevent reloading already-loaded components
// This prevents showing loading state when clicking the same route multiple times
// Key: route pathname or optional stable key (see LazyRoute routeCacheKey), Value: loaded component
const componentCache = new Map<string, React.ComponentType<any>>();

// LazyRoute wrapper: Navigates immediately, shows loading on target page
// Industry standard: Navigation should never be blocked by component loading
// Uses dynamic import with state to avoid React.lazy() blocking behavior
// Uses useMemo to memoize component loading and resets state on route change
// Caches loaded components to prevent reloading when clicking the same route
export const LazyRoute = ({
  loadComponent,
  allowedRoles: staticAllowedRoles,
  pageId,
  /** When set, chunk + component instance are cached under this key (e.g. Client Hub tab URLs). */
  routeCacheKey,
}: {
  loadComponent: () => Promise<{ default: React.ComponentType<any> }>;
  allowedRoles?: string[];
  pageId?: string;
  routeCacheKey?: string;
}) => {
  const location = useLocation();
  const [Component, setComponent] = React.useState<React.ComponentType<any> | null>(null);
  const [, setIsLoading] = React.useState(true);
  const [, setShowLoader] = React.useState(true);

  // Memoize the loadComponent function to prevent unnecessary re-renders
  const memoizedLoadComponent = useMemo(() => loadComponent, [loadComponent]);

  const cacheDependency = routeCacheKey ?? location.pathname;

  // Reset and load component when route changes
  React.useEffect(() => {
    // Prefer stable key so varying pathnames (e.g. /client-hub/overview) share one lazy instance
    const cacheKey = routeCacheKey ?? location.pathname;

    // Check if component is already cached for this route
    const cachedComponent = componentCache.get(cacheKey);

    if (cachedComponent) {
      // Component is already loaded - use it immediately, no loading state
      setComponent(() => cachedComponent);
      setIsLoading(false);
      setShowLoader(false);
      return;
    }

    // Component not cached - need to load it
    // Reset state immediately when route changes
    setComponent(null);
    setIsLoading(true);
    setShowLoader(true); // Always show loader to avoid blank flashes

    // Load component asynchronously without blocking navigation
    let isMounted = true;
    const pathAtEffectStart = location.pathname;

    // Keep a tiny delay for smoother perceived transitions (avoid rapid spinner flash)
    const loaderTimeout = setTimeout(() => {
      if (isMounted && location.pathname === pathAtEffectStart) {
        setShowLoader(true);
      }
    }, 80);

    memoizedLoadComponent()
      .then((module) => {
        if (!isMounted) return;
        // Without a stable route key, ignore stale responses when pathname changed mid-flight
        if (!routeCacheKey && location.pathname !== pathAtEffectStart) return;

        const loadedComponent = module.default;
        componentCache.set(cacheKey, loadedComponent);
        setComponent(() => loadedComponent);
        setIsLoading(false);
        setShowLoader(false); // Hide loader when component loads
      })
      .catch((error) => {
        if (!isMounted) return;
        if (!routeCacheKey && location.pathname !== pathAtEffectStart) return;
        logger.error("Failed to load route component", { error, context: "LazyRoute" });
        setIsLoading(false);
        setShowLoader(false);
      });

    // Cleanup function to prevent state updates after unmount or route change
    return () => {
      isMounted = false;
      clearTimeout(loaderTimeout);
    };
  }, [memoizedLoadComponent, cacheDependency]);

  // Render component or loading state
  const renderContent = () => {
    if (Component) {
      return <Component />;
    }
  };

  // Resolve roles: prefer dynamic from context, fall back to static prop
  const { getAllowedRoles } = usePagePermissions();
  const resolvedRoles = pageId ? getAllowedRoles(pageId) : staticAllowedRoles;

  return <ProtectedRoute allowedRoles={resolvedRoles}>{renderContent()}</ProtectedRoute>;
};

/**
 * Renders a <Navigate> to the first sub-page the current user can access.
 * Used for section index routes (/governance, /client-hub) that must
 * dynamically resolve a landing page based on RBAC permissions.
 */
export const RbacRedirect = ({
  subPages,
  fallback = "/error?status=403",
}: {
  subPages: { pageId: string; path: string }[];
  fallback?: string;
}) => {
  const { hasPageAccess } = usePagePermissions();
  const { role, loading, isAuthenticated } = useAuth();

  if (loading) return <Loader classname="default-loader" />;
  if (!isAuthenticated) return <Navigate to="/login" replace />;

  const target = subPages.find((sp) => hasPageAccess(sp.pageId, role))?.path ?? fallback;
  return <Navigate to={target} replace />;
};

// Configure router to prevent automatic scroll-to-top on navigation
// Industry standard: Preserve user's scroll position when navigating
const router = createBrowserRouter(
  [
    {
      path: "/",
      element: <Outlet />,
      errorElement: <ErrorPage />,
      children: [
        { index: true, element: <LoginPage /> },
        { path: "login", element: <LoginPage /> },
        { path: "2fa-verify", element: <TwoFactorVerifyPage /> },
        { path: "forgot-password", element: <ForgotPassword /> },
        { path: "reset-password", element: <ResetPassword /> },
        { path: "set-password", element: <SetPassword /> },
        { path: "error", element: <ErrorPage /> },
        { path: "*", element: <ErrorPage status={404} /> },
        {
          element: <AppShell />,
          children: [
            {
              path: "main",
              element: <LazyRoute loadComponent={loadMainPage} pageId="main" />,
            },
            {
              path: "executive-dashboard",
              element: (
                <LazyRoute loadComponent={loadExecutiveDashboardPage} pageId="executive-dashboard" />
              ),
            },
            // {
            //   path: "ueba",
            //   element: <LazyRoute loadComponent={loadUebaPage} allowedRoles={["Super Admin"]} />,
            // },
            {
              path: "threat-map",
              element: <LazyRoute loadComponent={loadThreatMPage} pageId="threat-map" />,
            },
            {
              path: "profile/:tabId?",
              element: (
                <LazyRoute
                  loadComponent={loadProfilePage}
                  pageId="profile"
                  routeCacheKey="/profile"
                />
              ),
            },
            {
              path: "posture-management",
              element: <Navigate to="/posture-management/overview" replace />,
            },
            {
              path: "posture-management/:tabId",
              element: (
                <LazyRoute loadComponent={loadPostureManagement} pageId="posture-management" />
              ),
            },
            // PARKED THE INCIDENTS PAGE 
            // {
            //   path: "incidents",
            //   element: (
            //     <LazyRoute
            //       loadComponent={loadIncidentsPage}
            //       allowedRoles={["Super Admin", "CSE"]}
            //     />
            //   ),
            // },
            // {
            //   path: "incidents-details",
            //   element: (
            //     <LazyRoute
            //       loadComponent={loadIncidentsDetails}
            //       allowedRoles={["Super Admin", "CSE"]}
            //     />
            //   ),
            // },
            {
              path: "nira",
              element: <LazyRoute loadComponent={loadAriaPage} pageId="nira" />,
            },
            ...(process.env.REACT_APP_ARIA_DIAGNOSTICS_PAGE === "true"
              ? [
                  {
                    path: "nira/diagnostics",
                    element: (
                      <LazyRoute
                        loadComponent={loadAriaDiagnosticsPage}
                        allowedRoles={["Super Admin", "CSM"]}
                      />
                    ),
                  },
                ]
              : []),
            {
              path: "client-hub",
              element: (
                <RbacRedirect
                  subPages={[
                    { pageId: "client-hub/overview", path: "/client-hub/overview" },
                    { pageId: "client-hub/product-maturity", path: "/client-hub/product-maturity" },
                    { pageId: "client-hub/roi-insights", path: "/client-hub/roi-insights" },
                    { pageId: "client-hub/qbr-planning", path: "/client-hub/qbr-planning" },
                  ]}
                />
              ),
            },
            {
              path: "client-hub/:tabId",
              element: (
                <LazyRoute
                  loadComponent={loadClientHub}
                  pageId="client-hub"
                  routeCacheKey="/client-hub"
                />
              ),
            },
            {
              path: "governance",
              element: (
                <RbacRedirect
                  subPages={[
                    { pageId: "governance/ai-adoption", path: "/governance/ai-adoption" },
                    { pageId: "governance/data-risks", path: "/governance/data-risks" },
                    { pageId: "governance/ai-inventory", path: "/governance/ai-inventory" },
                    { pageId: "governance/tprm", path: "/governance/tprm" },
                  ]}
                />
              ),
            },
            {
              path: "governance/ai-inventory",
              element: (
                <LazyRoute loadComponent={loadAIInventory} pageId="governance/ai-inventory" />
              ),
            },
            {
              path: "governance/ai-adoption",
              element: (
                <LazyRoute loadComponent={loadAIAdoption} pageId="governance/ai-adoption" />
              ),
            },
            {
              path: "governance/data-risks",
              element: (
                <LazyRoute loadComponent={loadDataRisks} pageId="governance/data-risks" />
              ),
            },
            // {
            //   path: "governance/control-map",
            //   element: <LazyRoute loadComponent={loadControlMap} allowedRoles={["Super Admin"]} />,
            // },
            // {
            //   path: "governance/awareness-tracker",
            //   element: (
            //     <LazyRoute loadComponent={loadAwarenessTracker} allowedRoles={["Super Admin"]} />
            //   ),
            // },
            // {
            //   path: "governance/policy-gaps",
            //   element: <LazyRoute loadComponent={loadPolicyGaps} allowedRoles={["Super Admin"]} />,
            // },
            // {
            //   path: "governance/evidence-vault",
            //   element: (
            //     <LazyRoute loadComponent={loadEvidenceVault} allowedRoles={["Super Admin"]} />
            //   ),
            // },
            {
              path: "help",
              element: <LazyRoute loadComponent={loadHelpPage} pageId="help" />,
            },
            {
              path: "governance/tprm",
              element: <LazyRoute loadComponent={loadTprmPage} pageId="governance/tprm" />,
            },

          ],
        },
      ],
    },
  ],
  {
    // Prevent automatic scroll-to-top on navigation
    // This preserves the user's scroll position when navigating between pages
    future: {
      v7_startTransition: true, // Use React 18 startTransition for navigation
    },
  }
);

export default function AppRouter() {
  // Prevent automatic scroll-to-top on route changes
  // This ensures the page doesn't scroll to top when navigating via sidebar
  useEffect(() => {
    // Override browser's default scroll restoration behavior
    if ("scrollRestoration" in globalThis.history) {
      globalThis.history.scrollRestoration = "manual";
    }
  }, []);

  return <RouterProvider router={router} />;
}
